import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { format, parse, parseISO, isAfter, isBefore, isEqual, isValid } from 'date-fns';
import { scheduleAPI } from '../services/api.ts';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import MobileDatePicker from '../components/MobileDatePicker.tsx';
import MapPicker from '../components/MapPicker.tsx';

interface ScheduleItem {
  id: string;
  date: string;
  time: string;
  location: string;
  address: string;
  status?: string;
  workFlowStatusId?: number; // 0=Pending, 1=Progress, 2=Completed
  projectName?: string;
  beforeImages?: string[];
  afterImages?: string[];
}

const History: React.FC = () => {
  // Get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // Get date from 30 days ago for default start date
  const getDefaultStartDate = () => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  };

  const [startDate, setStartDate] = useState(getDefaultStartDate());
  const [endDate, setEndDate] = useState(getTodayDate());
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'completed' | 'pending' | 'in-progress'>('all');
  const [schedules, setSchedules] = useState<ScheduleItem[]>([]);
  const [filteredSchedules, setFilteredSchedules] = useState<ScheduleItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const PAGE_SIZE = 10;
  
  // Date picker state
  const [showStartDatePicker, setShowStartDatePicker] = useState(false);
  const [showEndDatePicker, setShowEndDatePicker] = useState(false);
  const [startDateObj, setStartDateObj] = useState<Date | null>(new Date(startDate));
  const [endDateObj, setEndDateObj] = useState<Date | null>(new Date(endDate));
  
  // Add state for map picker
  const [showMapPicker, setShowMapPicker] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState('');
  
  // Function to map workFlowStatusId to status string
  const getStatusFromWorkFlowId = (workFlowStatusId?: number): 'pending' | 'completed' | 'in-progress' => {
    if (workFlowStatusId === 2) return 'completed';
    if (workFlowStatusId === 1) return 'in-progress';
    return 'pending'; // Default or workFlowStatusId === 0
  };
  
  // Format date for API
  const formatDateForApi = (dateString: string): string => {
    // Ensure date is in YYYY-MM-DD format for API
    return dateString;
  };
  
  // Safe date formatting function to handle potentially invalid date strings
  const safeFormatDate = (dateString: string, formatPattern: string): string => {
    try {
      if (!dateString) return 'N/A';
      
      // First try parsing as ISO string
      let parsedDate = parseISO(dateString);
      
      // If that fails, try parsing with the expected API format "DD MMM YYYY"
      if (!isValid(parsedDate)) {
        parsedDate = parse(dateString, 'dd MMM yyyy', new Date());
      }
      
      if (!isValid(parsedDate)) {
        console.error('Invalid date format:', dateString);
        return 'Invalid date';
      }
      
      return format(parsedDate, formatPattern);
    } catch (error) {
      console.error('Error formatting date:', error, dateString);
      return 'Invalid date';
    }
  };

  // Format time range for display
  const formatTimeRange = (timeString: string): string => {
    if (!timeString) return 'N/A';
    // The time string is already in the correct format "HH:MM AM - HH:MM AM"
    return timeString;
  };
  
  // Function to fetch schedules from API
  const fetchSchedules = async (isLoadMore = false) => {
    try {
      setLoading(true);
      setError(null);
      
      // Map status filter to workFlowStatusId
      let workFlowStatusId: number | undefined = undefined;
      if (statusFilter === 'completed') {
        workFlowStatusId = 2;
      } else if (statusFilter === 'pending') {
        workFlowStatusId = 0;
      } else if (statusFilter === 'in-progress') {
        workFlowStatusId = 1;
      }
      // For 'all', we don't specify workFlowStatusId, so it returns all statuses
      
      const params = {
        PageNumber: isLoadMore ? currentPage : 1,
        PageSize: PAGE_SIZE,
        StartDate: formatDateForApi(startDate),
        EndDate: formatDateForApi(endDate),
        IsFromHistory: true,
        // Only include WorkFlowStatusId if a specific status is selected
        ...(statusFilter !== 'all' && { WorkFlowStatusId: workFlowStatusId }),
        // Only include Address if searchText is not empty
        ...(searchText.trim() !== '' && { Address: searchText.trim() })
      };
      
      console.log('Fetching history with params:', params);
      
      const response = await scheduleAPI.getHistory(params);
      
      if (response && Array.isArray(response.data)) {
        // Process the response data to match our ScheduleItem interface
        const processedData = response.data.map((item: any) => ({
          id: item.id.toString(),
          date: item.date || '',
          time: item.time || '',
          location: item.location || item.projectName || 'Unknown Location',
          address: item.address || '',
          projectName: item.projectName,
          workFlowStatusId: item.workFlowStatusId,
          // Map the workFlowStatusId to a status string for display
          status: getStatusFromWorkFlowId(item.workFlowStatusId),
          beforeImages: item.beforeImages || [],
          afterImages: item.afterImages || []
        }));
        
        if (isLoadMore) {
          setSchedules(prev => [...prev, ...processedData]);
          setFilteredSchedules(prev => [...prev, ...processedData]);
        } else {
          setSchedules(processedData);
          setFilteredSchedules(processedData);
        }
        
        // Update pagination state
        setHasMore(processedData.length === PAGE_SIZE);
        if (isLoadMore) {
          setCurrentPage(prev => prev + 1);
        } else {
          setCurrentPage(1);
        }
      } else {
        console.error('Invalid response format:', response);
        setError('Failed to load history data. Please try again.');
      }
    } catch (error) {
      console.error('Error fetching schedule history:', error);
      setError('Failed to load history data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchSchedules();
  }, []);

  // Handle search
  const handleSearch = () => {
    // Now we'll call the API with the filters instead of filtering client-side
    fetchSchedules();
  };

  // Handle load more
  const handleLoadMore = () => {
    if (!loading && hasMore) {
      fetchSchedules(true);
    }
  };

  // Clear filters
  const clearFilters = () => {
    setStartDate(getDefaultStartDate());
    setEndDate(getTodayDate());
    setSearchText('');
    setStatusFilter('all');
    // Reset pagination
    setCurrentPage(1);
    setHasMore(true);
    // Call API with reset filters
    fetchSchedules();
  };

  // Function to format date for display
  const formatDateForDisplay = (date: Date | null): string => {
    if (!date) return '';
    return format(date, 'dd/MM/yyyy');
  };

  // Handle start date selection
  const handleStartDateSelect = (date: Date | null) => {
    setStartDateObj(date);
    if (date) {
      setStartDate(format(date, 'yyyy-MM-dd'));
    } else {
      setStartDate(getDefaultStartDate());
    }
    setShowStartDatePicker(false);
  };

  // Handle end date selection
  const handleEndDateSelect = (date: Date | null) => {
    setEndDateObj(date);
    if (date) {
      setEndDate(format(date, 'yyyy-MM-dd'));
    } else {
      setEndDate(getTodayDate());
    }
    setShowEndDatePicker(false);
  };

  // Function to handle address click
  const handleAddressClick = (address: string) => {
    setSelectedAddress(address);
    setShowMapPicker(true);
  };

  return (
    <div className="container-app py-6">
      <h1 className="text-2xl font-bold mb-6">Cleaning History</h1>

      {/* Search Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <h2 className="text-lg font-semibold mb-4">Search Filters</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <div 
              className="input flex items-center cursor-pointer"
              onClick={() => setShowStartDatePicker(true)}
            >
              <input
                type="text"
                readOnly
                className="flex-grow cursor-pointer bg-transparent border-0 p-0 focus:outline-none"
                value={formatDateForDisplay(startDateObj)}
                placeholder="Select start date"
              />
              <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          </div>
          <div>
            <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <div 
              className="input flex items-center cursor-pointer"
              onClick={() => setShowEndDatePicker(true)}
            >
              <input
                type="text"
                readOnly
                className="flex-grow cursor-pointer bg-transparent border-0 p-0 focus:outline-none"
                value={formatDateForDisplay(endDateObj)}
                placeholder="Select end date"
              />
              <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="mb-4">
          <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
            Search Location or Address
          </label>
          <input
            type="text"
            id="search"
            className="input"
            placeholder="Enter location or address..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
          <div className="flex flex-wrap gap-2">
            <button
              className={`px-3 py-1 rounded-full text-sm ${
                statusFilter === 'all'
                  ? 'bg-primary text-white'
                  : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
              }`}
              onClick={() => setStatusFilter('all')}
            >
              All
            </button>
            <button
              className={`px-3 py-1 rounded-full text-sm ${
                statusFilter === 'completed'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
              }`}
              onClick={() => setStatusFilter('completed')}
            >
              Completed
            </button>
            <button
              className={`px-3 py-1 rounded-full text-sm ${
                statusFilter === 'in-progress'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
              }`}
              onClick={() => setStatusFilter('in-progress')}
            >
              In Progress
            </button>
            <button
              className={`px-3 py-1 rounded-full text-sm ${
                statusFilter === 'pending'
                  ? 'bg-yellow-600 text-white'
                  : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
              }`}
              onClick={() => setStatusFilter('pending')}
            >
              Pending
            </button>
          </div>
        </div>

        <div className="flex justify-between">
          <button
            onClick={handleSearch}
            className="btn btn-primary"
          >
            Search
          </button>
          <button
            onClick={clearFilters}
            className="text-gray-600 hover:text-gray-900"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Results */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b">
          <h2 className="text-lg font-semibold">
            Results ({filteredSchedules.length})
          </h2>
        </div>
        
        {filteredSchedules.length > 0 ? (
          <div className="divide-y">
            {filteredSchedules.map((schedule) => (
              <div key={schedule.id} className="p-4 hover:bg-gray-50">
                <div className="flex flex-col md:flex-row justify-between">
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <div className="bg-blue-500 text-white p-1 rounded mr-2">
                        <span className="text-xs font-bold">{safeFormatDate(schedule.date, 'MMM d')}</span>
                      </div>
                      <span className="text-sm text-gray-600">{formatTimeRange(schedule.time)}</span>
                      <span className="ml-2">
                        <span className={`
                          inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium
                          ${schedule.status === 'completed' ? 'bg-green-100 text-green-800' : 
                            'bg-blue-100 text-blue-800'}
                        `}>
                          {schedule.status ? schedule.status.charAt(0).toUpperCase() + schedule.status.slice(1) : 'Unknown'}
                        </span>
                      </span>
                    </div>
                    <div className="flex flex-grow items-center justify-between">
                      <div className="truncate">
                        <h3 className="text-lg font-semibold text-gray-800">{schedule.location}</h3>
                        <p 
                          className="text-sm text-primary truncate cursor-pointer hover:underline flex items-center"
                          onClick={() => handleAddressClick(schedule.address)}
                        >
                          {schedule.address}
                          <svg className="w-3 h-3 ml-1 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                          </svg>
                        </p>
                        <p className="text-sm text-gray-500">
                          {safeFormatDate(schedule.date, 'MMM d, yyyy')} • {formatTimeRange(schedule.time)}
                        </p>
                      </div>
                      <div className="ml-4 flex-shrink-0">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            schedule.status === 'completed'
                              ? 'bg-green-100 text-green-800'
                              : schedule.status === 'in-progress'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}
                        >
                          {schedule.status === 'completed'
                            ? 'Completed'
                            : schedule.status === 'in-progress'
                            ? 'In Progress'
                            : 'Pending'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Before/After Images Preview */}
                  {(schedule.beforeImages?.length || schedule.afterImages?.length) ? (
                    <div className="mt-4 md:mt-0 md:ml-4 flex items-center">
                      <div className="flex space-x-3">
                        {schedule.beforeImages && schedule.beforeImages.length > 0 && (
                          <div className="relative">
                            <div className="w-16 h-16 rounded-md overflow-hidden bg-gray-100">
                              <img
                                src={schedule.beforeImages[0]}
                                alt="Before cleaning"
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <div className="absolute -top-2 -right-2 bg-primary text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                              {schedule.beforeImages.length}
                            </div>
                            <div className="text-xs text-center mt-1 text-gray-600">Before</div>
                            
                            {/* Additional image hints if more than one image */}
                            {schedule.beforeImages.length > 1 && (
                              <div className="absolute bottom-0 right-0 left-0 h-4 bg-black bg-opacity-40 flex justify-center items-center">
                                <div className="flex space-x-0.5">
                                  {[...Array(Math.min(3, schedule.beforeImages.length))].map((_, i) => (
                                    <div key={i} className="w-1 h-1 bg-white rounded-full"></div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {schedule.afterImages && schedule.afterImages.length > 0 && (
                          <div className="relative">
                            <div className="w-16 h-16 rounded-md overflow-hidden bg-gray-100">
                              <img
                                src={schedule.afterImages[0]}
                                alt="After cleaning"
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <div className="absolute -top-2 -right-2 bg-green-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                              {schedule.afterImages.length}
                            </div>
                            <div className="text-xs text-center mt-1 text-gray-600">After</div>
                            
                            {/* Additional image hints if more than one image */}
                            {schedule.afterImages.length > 1 && (
                              <div className="absolute bottom-0 right-0 left-0 h-4 bg-black bg-opacity-40 flex justify-center items-center">
                                <div className="flex space-x-0.5">
                                  {[...Array(Math.min(3, schedule.afterImages.length))].map((_, i) => (
                                    <div key={i} className="w-1 h-1 bg-white rounded-full"></div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : null}

                  <div className="mt-4 md:mt-0 md:ml-4 flex items-center">
                    <Link
                      to={`/schedule/${schedule.id}`}
                      className="btn btn-primary text-sm"
                    >
                      View Details
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-6 text-center text-gray-500">
            No schedules found matching your search criteria.
          </div>
        )}
      </div>
      
      {/* Mobile Date Pickers */}
      <MobileDatePicker
        isOpen={showStartDatePicker}
        onClose={() => setShowStartDatePicker(false)}
        onSelectDate={handleStartDateSelect}
        selectedDate={startDateObj}
        initialDate={startDateObj || new Date()}
      />
      
      <MobileDatePicker
        isOpen={showEndDatePicker}
        onClose={() => setShowEndDatePicker(false)}
        onSelectDate={handleEndDateSelect}
        selectedDate={endDateObj}
        initialDate={endDateObj || new Date()}
      />

      {/* Map Picker Modal */}
      {showMapPicker && (
        <MapPicker
          isOpen={showMapPicker}
          onClose={() => setShowMapPicker(false)}
          address={selectedAddress}
        />
      )}
    </div>
  );
};

export default History; 